// ═══════════════════════════════════════════════════════════════
// RegulationsAdminView.tsx – Központi Jogszabálygyűjtemény
// Törzsadattár, Nemzeti Jogtár hivatkozások, dinamikus hozzárendelések,
// Többféle nézet (Kártyák / Táblázat / Kinyíló lista) és Hivatkozáskezelő
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type {
  LegalRegulation,
  RegulationCategory,
  RegulationReferenceItem,
  CandidateRecordItem,
} from '../../types';
import { useAuth } from '../../context/AuthContext';
import { usePersistedViewMode } from '../../hooks/usePersistedViewMode';
import {
  Scale,
  Plus,
  Search,
  ExternalLink,
  Edit2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Sparkles,
  Link2,
  LayoutGrid,
  Table,
  Layers,
  ChevronDown,
  ChevronRight,
  X,
  Paperclip,
  Save,
  Unlink,
  Calendar,
} from 'lucide-react';

const CATEGORIES: { id: RegulationCategory | 'all'; label: string; color: string }[] = [
  { id: 'all', label: 'Összes kategória', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  { id: 'kozigazgatas', label: 'Közigazgatási eljárás', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { id: 'onkormanyzat', label: 'Önkormányzati működés', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' },
  { id: 'ado', label: 'Adóigazgatás & Illetékek', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  { id: 'anyakonyv', label: 'Anyakönyvi igazgatás', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
  { id: 'gazdalkodas', label: 'Gazdálkodás & Pénzügy', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { id: 'adatvedelem', label: 'Közadat & Információszabadság', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300' },
  { id: 'altalanos', label: 'Általános / Alaptörvény', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
];

const referencesCache = new Map<number, RegulationReferenceItem[]>();

interface RegulationReferenceBadgeWithHoverProps {
  item: LegalRegulation;
  onOpenReferences: (item: LegalRegulation) => void;
  placement?: 'top' | 'bottom';
  className?: string;
}

export const RegulationReferenceBadgeWithHover: React.FC<RegulationReferenceBadgeWithHoverProps> = ({
  item,
  onOpenReferences,
  placement = 'bottom',
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewItems, setPreviewItems] = useState<RegulationReferenceItem[] | null>(null);
  const hoverTimerRef = React.useRef<any>(null);
  const badgeRef = React.useRef<HTMLDivElement>(null);

  const usageCount = item.usage_count || 0;

  const handleMouseEnter = () => {
    if (usageCount === 0) return;
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(async () => {
      setIsHovered(true);
      if (referencesCache.has(item.id)) {
        setPreviewItems(referencesCache.get(item.id)!);
      } else {
        setLoadingPreview(true);
        try {
          const res = await ApiClient.getRegulationReferences(item.id);
          if (res.ok && res.data) {
            referencesCache.set(item.id, res.data);
            setPreviewItems(res.data);
          }
        } catch {
          // ignore
        } finally {
          setLoadingPreview(false);
        }
      }
    }, 120);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);
    setIsHovered(false);
  };

  return (
    <div
      ref={badgeRef}
      className={`relative inline-block ${isHovered ? 'z-50' : 'z-10'} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenReferences(item);
        }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:hover:bg-purple-900/50 dark:text-purple-300 border border-purple-200 dark:border-purple-800/70 transition cursor-pointer shadow-2xs group"
        title="Hivatkozó pontok megtekintése és kezelése (Egérrel rámutatva gyorslista)"
      >
        <Link2 className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform" />
        <span>{usageCount} pontban hivatkozva</span>
      </button>

      {/* Lebegő Előnézet Popover */}
      {isHovered && usageCount > 0 && (
        <div
          className={`absolute z-50 left-1/2 -translate-x-1/2 ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } w-84 p-3.5 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-purple-200 dark:border-purple-800/80 text-left text-xs pointer-events-none animate-in fade-in zoom-in-95 duration-150`}
        >
          <div className="flex items-center justify-between font-bold text-[11px] text-slate-700 dark:text-slate-200 pb-2 border-b border-purple-100 dark:border-slate-800 mb-2.5">
            <span className="flex items-center gap-1.5 text-purple-700 dark:text-purple-300">
              <Link2 className="w-3.5 h-3.5" />
              <span>Hivatkozó közzétételi pontok ({usageCount} db)</span>
            </span>
            <span className="text-[9px] text-slate-400 font-normal">Kattintson a szerkesztéshez</span>
          </div>

          {loadingPreview ? (
            <div className="py-3 text-center text-slate-400 flex items-center justify-center gap-2 text-xs">
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-600 dark:text-purple-400" />
              <span>Pontok betöltése...</span>
            </div>
          ) : previewItems && previewItems.length > 0 ? (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {previewItems.slice(0, 8).map((ref) => {
                const d = ref.parsedData || {};
                const pont = d.pont || d.kod || '–';
                const cim = d.cim || d.title || ref.page_title || 'Névtelen pont';
                return (
                  <li
                    key={ref.record_id}
                    className="flex items-start gap-2 p-1.5 rounded-lg bg-purple-50/50 dark:bg-purple-950/30 border border-purple-100 dark:border-purple-900/40 text-[11px]"
                  >
                    <span className="font-mono font-bold text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 shrink-0 text-[10px]">
                      {pont}
                    </span>
                    {ref.year ? (
                      <span className="font-mono font-bold text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 shrink-0 text-[10px]">
                        {ref.year}
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400 font-semibold shrink-0">
                        Állandó
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 dark:text-slate-200 truncate">
                        {cim}
                      </div>
                      {ref.page_title && (
                        <div className="text-[10px] text-slate-400 truncate">{ref.page_title}</div>
                      )}
                      {ref.section_note && (
                        <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono mt-0.5">
                          {ref.section_note}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
              {previewItems.length > 8 && (
                <li className="text-[10px] text-center font-bold text-purple-600 dark:text-purple-400 pt-1">
                  + további {previewItems.length - 8} hivatkozó pont...
                </li>
              )}
            </ul>
          ) : (
            <div className="py-2 text-center text-slate-400 text-xs">Nincsenek elérhető pontok</div>
          )}
        </div>
      )}
    </div>
  );
};

export const RegulationsAdminView: React.FC = () => {
  const { role, user } = useAuth();
  const isSuperAdmin = role === 'superadmin' || role === 'tenant_admin' || user?.role === 'superadmin' || user?.role === 'tenant_admin';
  const canEdit = ['superadmin', 'tenant_admin', 'org_admin', 'editor'].includes(role) || isSuperAdmin;

  const [regulations, setRegulations] = useState<LegalRegulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<RegulationCategory | 'all'>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [viewMode, setViewMode] = usePersistedViewMode<'grid' | 'table' | 'accordion'>(
    'regulations',
    'grid',
    ['grid', 'table', 'accordion']
  );
  const [expandedAccordionIds, setExpandedAccordionIds] = useState<number[]>([]);

  // Jogszabály Szerkesztő Modál állapot
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<LegalRegulation> | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Hivatkozások Kezelő Modál állapot ("X pontban hivatkozva")
  const [activeRefReg, setActiveRefReg] = useState<LegalRegulation | null>(null);
  const [refItems, setRefItems] = useState<RegulationReferenceItem[]>([]);
  const [refLoading, setRefLoading] = useState(false);
  const [candidates, setCandidates] = useState<CandidateRecordItem[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | ''>('');
  const [newSectionNote, setNewSectionNote] = useState('');
  const [isAddingRef, setIsAddingRef] = useState(false);

  // Gyors rekord szerkesztő állapot a modálon belül
  const [quickEditRecord, setQuickEditRecord] = useState<RegulationReferenceItem | null>(null);
  const [quickEditData, setQuickEditData] = useState<Record<string, any>>({});
  const [isSavingQuickEdit, setIsSavingQuickEdit] = useState(false);

  const loadData = async () => {
    setLoading(true);
    const res = await ApiClient.getRegulations({
      search: search.trim() || undefined,
      category: selectedCategory !== 'all' ? selectedCategory : undefined,
      year: selectedYear !== 'all' ? selectedYear : undefined,
    });
    if (res.ok && res.data) {
      setRegulations(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [selectedCategory, selectedYear]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadData();
  };

  const handleSeed = async () => {
    if (!confirm('Betölti az alapértelmezett magyar közigazgatási jogszabálytörzset (Ákr., Mötv., Infotv., Itv., Áht., stb.) a Nemzeti Jogtár (njt.hu) linkekkel?')) {
      return;
    }
    setLoading(true);
    const res = await ApiClient.seedRegulations();
    if (res.ok) {
      setMsg({ type: 'success', text: `Sikeresen betöltve a törzsjogszabályok! (${res.inserted ?? 0} új)` });
      await loadData();
    } else {
      setMsg({ type: 'error', text: res.error || 'Hiba a betöltéskor' });
    }
    setLoading(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem?.code || !editingItem?.title || !editingItem?.njt_url) {
      alert('Kód, megnevezés és Nemzeti Jogtár URL megadása kötelező!');
      return;
    }

    setSaving(true);
    let res;
    if (editingItem.id) {
      res = await ApiClient.updateRegulation(editingItem.id, editingItem);
    } else {
      res = await ApiClient.createRegulation(editingItem);
    }

    if (res.ok) {
      setMsg({ type: 'success', text: editingItem.id ? 'Jogszabály sikeresen frissítve!' : 'Új jogszabály rögzítve!' });
      setModalOpen(false);
      setEditingItem(null);
      await loadData();
    } else {
      alert('Hiba a mentéskor: ' + (res.error || 'Ismeretlen hiba'));
    }
    setSaving(false);
  };

  const handleDelete = async (item: LegalRegulation) => {
    if (!confirm(`Biztosan törölni szeretné a(z) "${item.short_title || item.title}" jogszabályt?`)) return;

    const res = await ApiClient.deleteRegulation(item.id);
    if (res.ok) {
      setMsg({ type: 'success', text: 'Jogszabály törölve!' });
      await loadData();
    } else {
      alert('Hiba: ' + (res.error || 'Nem törölhető'));
    }
  };

  // ── HIVATKOZÁSOK KEZELÉSE ──────────────────────────────────────
  const extractRefs = (res: any): RegulationReferenceItem[] => {
    if (!res) return [];
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.data)) return res.data.data;
    if (Array.isArray(res)) return res;
    return [];
  };

  const handleOpenReferences = async (reg: LegalRegulation) => {
    setActiveRefReg(reg);
    setRefLoading(true);
    setRefItems([]);
    setQuickEditRecord(null);
    try {
      const [refsRes, candRes] = await Promise.all([
        ApiClient.getRegulationReferences(reg.id),
        ApiClient.getCandidateRecords(),
      ]);
      setRefItems(extractRefs(refsRes));
      if (candRes.ok && candRes.data) {
        const cands = Array.isArray(candRes.data) ? candRes.data : ((candRes.data as any)?.data || []);
        setCandidates(cands);
      }
    } catch (err) {
      console.error('Hiba a hivatkozások betöltésekor:', err);
    } finally {
      setRefLoading(false);
    }
  };

  const handleUnlinkReference = async (recordId: number, pointTitle: string) => {
    if (!activeRefReg) return;
    if (!confirm(`Biztosan leválasztja a(z) "${activeRefReg.short_title || activeRefReg.code}" jogszabályt a(z) "${pointTitle}" pontról?`)) return;

    const res = await ApiClient.removeRegulationReference(activeRefReg.id, recordId);
    if (res.ok) {
      referencesCache.delete(activeRefReg.id);
      const refsRes = await ApiClient.getRegulationReferences(activeRefReg.id);
      setRefItems(extractRefs(refsRes));
      loadData();
    } else {
      alert(res.error || 'Hiba a leválasztás során');
    }
  };

  const handleAddReference = async () => {
    if (!activeRefReg || !selectedCandidateId) return;
    setIsAddingRef(true);
    const res = await ApiClient.addRegulationReference(
      activeRefReg.id,
      Number(selectedCandidateId),
      newSectionNote.trim() || undefined
    );
    if (res.ok) {
      const refsRes = await ApiClient.getRegulationReferences(activeRefReg.id);
      setRefItems(extractRefs(refsRes));
      setSelectedCandidateId('');
      setNewSectionNote('');
      loadData();
    } else {
      alert(res.error || 'Hiba a hozzárendelés során');
    }
    setIsAddingRef(false);
  };

  const handleStartQuickEdit = (item: RegulationReferenceItem) => {
    setQuickEditRecord(item);
    setQuickEditData({
      ...(item.parsedData || {}),
      section_note: item.section_note || '',
    });
  };

  const handleSaveQuickEdit = async () => {
    if (!quickEditRecord || !activeRefReg) return;
    setIsSavingQuickEdit(true);
    try {
      const { section_note, ...recordData } = quickEditData;
      const saveRes = await ApiClient.saveRecord(quickEditRecord.page_id, recordData, quickEditRecord.record_id);
      if (section_note !== quickEditRecord.section_note) {
        await ApiClient.addRegulationReference(activeRefReg.id, quickEditRecord.record_id, section_note);
      }
      if (saveRes.ok) {
        const refsRes = await ApiClient.getRegulationReferences(activeRefReg.id);
        setRefItems(extractRefs(refsRes));
        setQuickEditRecord(null);
        loadData();
      } else {
        alert(saveRes.error || 'Hiba a mentés során');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSavingQuickEdit(false);
    }
  };

  const toggleAccordion = (id: number) => {
    setExpandedAccordionIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const getCategoryBadge = (cat: RegulationCategory) => {
    const item = CATEGORIES.find(c => c.id === cat) || CATEGORIES[CATEGORIES.length - 1];
    return (
      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${item.color}`}>
        {item.label}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Fejléc */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/20">
            <Scale className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              Központi Jogszabálygyűjtemény
              <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 text-xs font-semibold border border-blue-200 dark:border-blue-900">
                {regulations.length} jogszabály
              </span>
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Nemzeti Jogtár (njt.hu) hivatkozások, közjogi háttér és dinamikus rendelés a közzétételi pontokhoz
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <button
              onClick={handleSeed}
              className="px-3 py-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-800 rounded-xl transition flex items-center gap-1.5 cursor-pointer"
              title="Alapértelmezett jogszabályok betöltése"
            >
              <Sparkles className="w-4 h-4" />
              <span>Törzsjogszabályok Betöltése</span>
            </button>
          )}

          {canEdit && (
            <button
              onClick={() => {
                setEditingItem({
                  code: '',
                  title: '',
                  short_title: '',
                  category: 'kozigazgatas',
                  njt_url: 'https://njt.hu/jogszabaly/',
                  description: '',
                  is_system: isSuperAdmin ? 1 : 0,
                });
                setModalOpen(true);
              }}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-semibold shadow-md shadow-indigo-500/20 transition flex items-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Új Jogszabály</span>
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div
          className={`p-4 rounded-xl text-xs font-medium flex items-center justify-between border ${
            msg.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
              : 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {msg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span>{msg.text}</span>
          </div>
          <button onClick={() => setMsg(null)} className="text-xs opacity-60 hover:opacity-100 font-bold">✕</button>
        </div>
      )}

      {/* Keresés, Kategória Szűrők & Nézetváltó */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés jogszabály neve, száma (pl. 2016. évi CL.), rövidítése (Ákr.) vagy leírása szerint..."
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer"
          >
            Keresés
          </button>
        </form>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800/80">
          <div className="flex flex-wrap items-center gap-2">
            {/* Évszűrő kiválasztó */}
            <div className="flex items-center gap-1.5 bg-amber-50/80 dark:bg-amber-950/40 px-2.5 py-1.5 rounded-xl border border-amber-200/80 dark:border-amber-800/60 text-xs">
              <Calendar className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <span className="text-[11px] font-bold text-amber-900 dark:text-amber-200">Jogalap éve:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-700/80 text-slate-800 dark:text-slate-200 text-xs rounded-lg px-2 py-0.5 font-semibold focus:outline-none cursor-pointer"
              >
                <option value="all">Minden év (Összes jogalap)</option>
                <option value="2026">2026 (Aktuális / Most)</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="permanent">Állandó / Folyamatos</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer flex items-center gap-1.5 ${
                    selectedCategory === cat.id
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-slate-50 dark:bg-slate-950/60 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800'
                  }`}
                >
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nézetváltó Gombok */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Kártyás nézet"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kártyák</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Táblázatos nézet"
            >
              <Table className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Táblázat</span>
            </button>
            <button
              onClick={() => setViewMode('accordion')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                viewMode === 'accordion'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              title="Kinyíló lista nézet"
            >
              <Layers className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kinyíló lista</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tartalom megjelenítés a kiválasztott nézet szerint */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <span className="text-xs">Jogszabályok betöltése...</span>
        </div>
      ) : regulations.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center">
          <Scale className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Nincs megjeleníthető jogszabály</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mt-1 mb-4">
            A szűrési feltételeknek megfelelő törvény vagy rendelet nem található, vagy a gyűjtemény még üres.
          </p>
          {canEdit && (
            <button
              onClick={handleSeed}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold transition"
            >
              Alapértelmezett Törzsadatok Betöltése
            </button>
          )}
        </div>
      ) : viewMode === 'table' ? (
        /* ── 1. TÁBLÁZATOS NÉZET ────────────────────────────────────────── */
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950/70 text-slate-500 font-mono uppercase text-[10px] border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="py-3 px-4 font-bold w-24">Rövidítés</th>
                  <th className="py-3 px-4 font-bold w-28">Kód</th>
                  <th className="py-3 px-4 font-bold min-w-[280px]">Jogszabály Megnevezése</th>
                  <th className="py-3 px-4 font-bold w-40">Kategória</th>
                  <th className="py-3 px-4 text-center font-bold w-36">Hivatkozások</th>
                  <th className="py-3 px-4 text-center font-bold w-24">Jogtár</th>
                  <th className="py-3 px-4 text-right font-bold w-24">Műveletek</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {regulations.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                    <td className="py-3 px-4 font-bold font-mono text-indigo-600 dark:text-indigo-400">
                      {item.short_title ? (
                        <span className="px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900">
                          {item.short_title}
                        </span>
                      ) : (
                        <span className="text-slate-400">–</span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-500 text-[11px]">
                      {item.code}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{item.title}</div>
                      {item.description && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-md mt-0.5">
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {getCategoryBadge(item.category)}
                      {item.referenced_years && (
                        <div className="text-[10px] font-mono font-bold text-amber-700 dark:text-amber-400 mt-1">
                          📅 {item.referenced_years.split(',').join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <RegulationReferenceBadgeWithHover
                        item={item}
                        onOpenReferences={handleOpenReferences}
                      />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <a
                        href={item.njt_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/60 border border-blue-200 dark:border-blue-900 transition"
                      >
                        <span>njt.hu</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        {canEdit && (
                          <button
                            onClick={() => { setEditingItem(item); setModalOpen(true); }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                            title="Módosítás"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canEdit && (!item.is_system || isSuperAdmin) && (
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-lg hover:bg-rose-50 dark:hover:bg-slate-800 transition cursor-pointer"
                            title="Törlés"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : viewMode === 'accordion' ? (
        /* ── 2. KINYÍLÓ LISTÁS NÉZET (ACCORDION) ────────────────────────── */
        <div className="space-y-3">
          {regulations.map((item) => {
            const isOpen = expandedAccordionIds.includes(item.id);
            return (
              <div
                key={item.id}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs hover:border-slate-300 dark:hover:border-slate-700 transition"
              >
                <div
                  onClick={() => toggleAccordion(item.id)}
                  className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 cursor-pointer select-none hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <button className="p-1 text-slate-400 hover:text-slate-600 transition shrink-0">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-indigo-600" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {item.short_title && (
                          <span className="px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold text-xs font-mono border border-indigo-100 dark:border-indigo-900">
                            {item.short_title}
                          </span>
                        )}
                        <span className="font-mono text-[11px] text-slate-400">{item.code}</span>
                        {getCategoryBadge(item.category)}
                        {item.is_system === 1 && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-500 font-mono">
                            Törzs
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-sm text-slate-900 dark:text-white truncate">
                        {item.title}
                      </h3>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 self-end md:self-center" onClick={(e) => e.stopPropagation()}>
                    <RegulationReferenceBadgeWithHover
                      item={item}
                      onOpenReferences={handleOpenReferences}
                    />

                    <a
                      href={item.njt_url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2.5 py-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-950/50 transition flex items-center gap-1 text-xs font-semibold border border-blue-100 dark:border-blue-900/50"
                      title="Megnyitás a Nemzeti Jogtárban"
                    >
                      <span>njt.hu</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>

                    {canEdit && (
                      <button
                        onClick={() => { setEditingItem(item); setModalOpen(true); }}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        title="Szerkesztés"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {canEdit && (!item.is_system || isSuperAdmin) && (
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-xl hover:bg-rose-50 dark:hover:bg-slate-800 transition cursor-pointer"
                        title="Törlés"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Kinyitott Tartalom */}
                {isOpen && (
                  <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40 space-y-4">
                    {item.description && (
                      <div>
                        <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                          Törvényi háttér és leírás
                        </h5>
                        <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
                      <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
                        <span>Hivatkozó közzétételi pontok száma: <b className="text-purple-600 dark:text-purple-400">{item.usage_count || 0} pont</b></span>
                        {item.referenced_years && (
                          <span className="text-[10px] font-mono font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900/50 px-2 py-0.5 rounded-md">
                            📅 Évek: {item.referenced_years.split(',').join(', ')}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleOpenReferences(item)}
                        className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-xs flex items-center gap-1.5 transition cursor-pointer"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        <span>Hivatkozások Részletes Kezelése</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── 3. RÁCSOS / KÁRTYÁS NÉZET (GRID) ────────────────────────────── */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {regulations.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs hover:shadow-md transition flex flex-col justify-between group"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {getCategoryBadge(item.category)}
                    {item.is_system === 1 && (
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-500 font-mono" title="Rendszerszintű törzs">
                        Törzs
                      </span>
                    )}
                  </div>
                  {item.short_title && (
                    <span className="px-2 py-0.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold text-xs border border-indigo-100 dark:border-indigo-900">
                      {item.short_title}
                    </span>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-snug line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition">
                    {item.title}
                  </h3>
                  <div className="text-[11px] font-mono text-slate-400 mt-0.5">
                    Kód: {item.code}
                  </div>
                </div>

                {item.description && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">
                    {item.description}
                  </p>
                )}
              </div>

              <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-2">
                <div className="flex flex-col gap-1 items-start">
                  <RegulationReferenceBadgeWithHover
                    item={item}
                    onOpenReferences={handleOpenReferences}
                  />
                  {item.referenced_years && (
                    <span className="text-[10px] font-mono font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900/50 px-1.5 py-0.5 rounded">
                      📅 {item.referenced_years.split(',').join(', ')}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <a
                    href={item.njt_url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-lg transition flex items-center gap-1 text-xs font-semibold"
                    title="Megnyitás a Nemzeti Jogtárban (njt.hu)"
                  >
                    <span>njt.hu</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>

                  {canEdit && (
                    <>
                      <button
                        onClick={() => {
                          setEditingItem(item);
                          setModalOpen(true);
                        }}
                        className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer"
                        title="Szerkesztés"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {(!item.is_system || isSuperAdmin) && (
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition cursor-pointer"
                          title="Törlés"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── HIVATKOZÓ PONTOK RÉSZLETES KEZELŐ MODÁLJA ───────────────────── */}
      {activeRefReg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150">
            {/* Fejléc */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-950/40 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center font-bold">
                  <Link2 className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-base text-slate-900 dark:text-white">
                      [{activeRefReg.short_title || activeRefReg.code}] Hivatkozó Közzétételi Pontok
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 font-semibold text-xs border border-purple-200 dark:border-purple-800">
                      {refLoading ? 'Betöltés...' : `${refItems.length} pont`}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xl">
                    {activeRefReg.title}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setActiveRefReg(null); setQuickEditRecord(null); }}
                className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Test / Görgethető tartalom */}
            <div className="p-6 overflow-y-auto space-y-5 flex-1">
              {/* Új hivatkozás hozzáadása */}
              {canEdit && (
                <div className="bg-slate-50 dark:bg-slate-950/50 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                    <Plus className="w-4 h-4 text-indigo-500" />
                    <span>Új közzétételi pont összekapcsolása ezzel a jogszabállyal</span>
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                    <div className="sm:col-span-7">
                      <select
                        value={selectedCandidateId}
                        onChange={(e) => setSelectedCandidateId(e.target.value ? Number(e.target.value) : '')}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">-- Válasszon egy közzétételi pontot... --</option>
                        {candidates.map(c => (
                          <option key={c.id} value={c.id}>
                            [{c.year ? `${c.year} • ` : ''}{c.page_title}] {c.pont} {c.cim}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-3">
                      <input
                        type="text"
                        value={newSectionNote}
                        onChange={(e) => setNewSectionNote(e.target.value)}
                        placeholder="Szakasz (pl. 14. §)"
                        className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <button
                        onClick={handleAddReference}
                        disabled={!selectedCandidateId || isAddingRef}
                        className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs"
                      >
                        {isAddingRef ? '...' : 'Hozzáadás'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Rekord gyorsszerkesztő ablak ha meg van nyitva */}
              {quickEditRecord && (
                <div className="bg-indigo-50/70 dark:bg-indigo-950/40 p-4 rounded-xl border border-indigo-200 dark:border-indigo-800 space-y-3 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <h5 className="text-xs font-bold text-indigo-950 dark:text-indigo-200 flex items-center gap-1.5">
                      <Edit2 className="w-3.5 h-3.5 text-indigo-600" />
                      <span>Közzétételi Pont Gyorsszerkesztése (#{quickEditRecord.record_id})</span>
                    </h5>
                    <button
                      onClick={() => setQuickEditRecord(null)}
                      className="text-xs text-indigo-500 hover:underline cursor-pointer"
                    >
                      Mégse
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                        Pont sorszáma
                      </label>
                      <input
                        type="text"
                        value={quickEditData.pont || ''}
                        onChange={(e) => setQuickEditData({ ...quickEditData, pont: e.target.value })}
                        className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                        Jogszabályi szakasz / megjegyzés ennél a hivatkozásnál
                      </label>
                      <input
                        type="text"
                        value={quickEditData.section_note || ''}
                        onChange={(e) => setQuickEditData({ ...quickEditData, section_note: e.target.value })}
                        placeholder="pl. 15. § (2) bekezdés"
                        className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Megnevezés / Cím
                    </label>
                    <input
                      type="text"
                      value={quickEditData.cim || quickEditData.megnevezes || ''}
                      onChange={(e) => setQuickEditData({ ...quickEditData, cim: e.target.value, megnevezes: e.target.value })}
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Közzétett adat szövege / Leírás
                    </label>
                    <textarea
                      rows={2}
                      value={quickEditData.leiras || quickEditData.ertek || ''}
                      onChange={(e) => setQuickEditData({ ...quickEditData, leiras: e.target.value, ertek: e.target.value })}
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-xs resize-none"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setQuickEditRecord(null)}
                      className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
                    >
                      Mégse
                    </button>
                    <button
                      onClick={handleSaveQuickEdit}
                      disabled={isSavingQuickEdit}
                      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      <Save className="w-3.5 h-3.5" />
                      <span>{isSavingQuickEdit ? 'Mentés...' : 'Módosítás Mentése'}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Hivatkozó pontok listája */}
              {refLoading ? (
                <div className="py-12 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin text-purple-500" />
                  <span className="text-xs">Hivatkozások betöltése...</span>
                </div>
              ) : refItems.length === 0 ? (
                <div className="py-12 text-center text-slate-500 bg-slate-50 dark:bg-slate-950/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                  <Link2 className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                  <p className="text-xs font-medium">
                    Jelenleg egyetlen közzétételi pont sem hivatkozik erre a jogszabályra.
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Használja a fenti űrlapot egy közzétételi pont hozzárendeléséhez.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {refItems.map((ref) => {
                    const d = ref.parsedData || {};
                    const pont = d.pont || `${ref.sort_order}. pont`;
                    const cim = d.megnevezes || d.cim || d.nev || '–';
                    const leiras = d.leiras || d.ertek || '';

                    return (
                      <div
                        key={ref.record_id}
                        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-2xs hover:border-purple-300 dark:hover:border-purple-800/80 transition space-y-2.5"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 text-[10px] font-bold border border-blue-200/80 dark:border-blue-900/60">
                              {ref.tenant_name ? `${ref.tenant_name} • ` : ''}{ref.page_title}
                            </span>
                            {ref.year ? (
                              <span className="px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 text-[10px] font-bold border border-amber-200/80 dark:border-amber-900/60 font-mono">
                                📅 {ref.year}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-semibold">
                                Állandó
                              </span>
                            )}
                            <span className="font-mono font-bold text-xs text-slate-900 dark:text-white">
                              {pont}
                            </span>
                            <span className="font-semibold text-xs text-slate-800 dark:text-slate-200">
                              {cim}
                            </span>
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                            {ref.attachment_count > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-mono">
                                <Paperclip className="w-3 h-3" />
                                <span>{ref.attachment_count} db</span>
                              </span>
                            )}

                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleStartQuickEdit(ref)}
                                  className="px-2.5 py-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition flex items-center gap-1 cursor-pointer"
                                  title="Pont adatainak szerkesztése"
                                >
                                  <Edit2 className="w-3 h-3" />
                                  <span>Szerkesztés</span>
                                </button>
                                <button
                                  onClick={() => handleUnlinkReference(ref.record_id, `${pont} ${cim}`)}
                                  className="px-2.5 py-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition flex items-center gap-1 cursor-pointer"
                                  title="Jogszabály leválasztása erről a pontról"
                                >
                                  <Unlink className="w-3 h-3" />
                                  <span>Leválasztás</span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {ref.section_note && (
                          <div className="text-[11px] text-purple-700 dark:text-purple-300 bg-purple-50/60 dark:bg-purple-950/40 px-2.5 py-1 rounded-md border border-purple-100 dark:border-purple-900/60 inline-block font-mono">
                            📌 Vonatkozó szakasz: <b>{ref.section_note}</b>
                          </div>
                        )}

                        {leiras && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                            {leiras}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Lábléc */}
            <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-950/40 shrink-0">
              <span className="text-xs text-slate-500">
                A módosítások és leválasztások azonnal érvénybe lépnek az adatbázisban.
              </span>
              <button
                onClick={() => { setActiveRefReg(null); setQuickEditRecord(null); }}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── JOGSZABÁLY MÓDOSÍTÓ / LÉTREHOZÓ MODÁL ────────────────────────── */}
      {modalOpen && editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/40">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold">
                  <Scale className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {editingItem.id ? 'Jogszabály Módosítása' : 'Új Jogszabály Felvitele'}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Nemzeti Jogtár link és közzétételi törzsadat
                  </p>
                </div>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Azonosító Kód (pl. 2016-CL) *
                  </label>
                  <input
                    type="text"
                    required
                    value={editingItem.code || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, code: e.target.value })}
                    placeholder="pl. 2016-CL vagy 1990-XCIII"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Rövidítés / Hivatalos megjelölés
                  </label>
                  <input
                    type="text"
                    value={editingItem.short_title || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, short_title: e.target.value })}
                    placeholder="pl. Ákr., Mötv., Infotv., Itv."
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Jogszabály Teljes Hivatalos Megnevezése *
                </label>
                <input
                  type="text"
                  required
                  value={editingItem.title || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  placeholder="pl. Az általános közigazgatási rendtartásról szóló 2016. évi CL. törvény"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Kategória
                  </label>
                  <select
                    value={editingItem.category || 'altalanos'}
                    onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value as RegulationCategory })}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white"
                  >
                    {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Hatályos Nemzeti Jogtár URL (njt.hu) *
                  </label>
                  <input
                    type="url"
                    required
                    value={editingItem.njt_url || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, njt_url: e.target.value })}
                    placeholder="https://njt.hu/jogszabaly/..."
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-mono text-[11px]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Rövid Leírás / Vonatkozó Témakör
                </label>
                <textarea
                  rows={3}
                  value={editingItem.description || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  placeholder="Mire vonatkozik ez a törvény az önkormányzatnál vagy a közzétételi pontoknál..."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white resize-none"
                />
              </div>

              {isSuperAdmin && (
                <div className="flex items-center gap-2 p-3 bg-indigo-50/50 dark:bg-indigo-950/30 rounded-xl border border-indigo-100 dark:border-indigo-900/40">
                  <input
                    type="checkbox"
                    id="reg_is_system"
                    checked={Boolean(editingItem.is_system)}
                    onChange={(e) => setEditingItem({ ...editingItem, is_system: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <label htmlFor="reg_is_system" className="text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer">
                    Rendszerszintű törzsjogszabály (minden szervezet számára elérhető központi törvény)
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-md shadow-indigo-500/20 transition disabled:opacity-50 cursor-pointer"
                >
                  {saving ? 'Mentés...' : 'Jogszabály Mentése'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
